import { ref, computed, type Ref, type ComputedRef } from 'vue'
import { useRoute } from 'vue-router'
import {
  getActiveModal,
  getActiveBanners,
} from '@/services/advertisementService'
import type { Advertisement } from '@/types/advertisement'

const DISMISSED_CAMPAIGN_KEYS_STORAGE_KEY = 'dismissedAdvertisementCampaignKeys'

function readDismissedKeys(): string[] {
  try {
    const raw = localStorage.getItem(DISMISSED_CAMPAIGN_KEYS_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed)
      ? parsed.filter((v) => typeof v === 'string')
      : []
  } catch {
    return []
  }
}

function persistDismissedKey(dismissedKeys: Ref<string[]>, key: string): void {
  if (dismissedKeys.value.includes(key)) return
  dismissedKeys.value.push(key)
  localStorage.setItem(
    DISMISSED_CAMPAIGN_KEYS_STORAGE_KEY,
    JSON.stringify(dismissedKeys.value)
  )
}

export interface UseModalAdvertisement {
  modal: Ref<Advertisement | null>
  currentModal: ComputedRef<Advertisement | null>
  isLoading: Ref<boolean>
  hasError: Ref<boolean>
  fetchActiveModal: () => Promise<void>
  dismissCurrentModal: () => void
}

export function useModalAdvertisement(): UseModalAdvertisement {
  const modal = ref<Advertisement | null>(null)
  const isLoading = ref(false)
  const hasError = ref(false)
  const dismissedCampaignKeys = ref<string[]>(readDismissedKeys())

  const currentModal = computed<Advertisement | null>(() => {
    if (!modal.value) return null
    if (
      dismissedCampaignKeys.value.includes(modal.value.campaign_key)
    ) {
      return null
    }
    return modal.value
  })

  async function fetchActiveModal(): Promise<void> {
    isLoading.value = true
    hasError.value = false
    try {
      modal.value = await getActiveModal()
    } catch {
      hasError.value = true
      modal.value = null
    } finally {
      isLoading.value = false
    }
  }

  function dismissCurrentModal(): void {
    if (!modal.value) return
    persistDismissedKey(dismissedCampaignKeys, modal.value.campaign_key)
  }

  return {
    modal,
    currentModal,
    isLoading,
    hasError,
    fetchActiveModal,
    dismissCurrentModal,
  }
}

export interface UseBannerAdvertisements {
  banners: Ref<Advertisement[]>
  visibleBanners: ComputedRef<Advertisement[]>
  isLoading: Ref<boolean>
  hasError: Ref<boolean>
  fetchActiveBanners: () => Promise<void>
  dismissBanner: (banner: Advertisement) => void
}

export function useBannerAdvertisements(): UseBannerAdvertisements {
  const route = useRoute()
  const banners = ref<Advertisement[]>([])
  const isLoading = ref(false)
  const hasError = ref(false)
  const dismissedCampaignKeys = ref<string[]>(readDismissedKeys())

  // A banner is visible unless it has been dismissed or is restricted to
  // a different route
  const visibleBanners = computed<Advertisement[]>(() =>
    banners.value.filter(
      (banner) =>
        !dismissedCampaignKeys.value.includes(banner.campaign_key) &&
        (!banner.restricted_route_name ||
          route.name === banner.restricted_route_name)
    )
  )

  async function fetchActiveBanners(): Promise<void> {
    isLoading.value = true
    hasError.value = false
    try {
      banners.value = await getActiveBanners()
    } catch {
      hasError.value = true
      banners.value = []
    } finally {
      isLoading.value = false
    }
  }

  function dismissBanner(banner: Advertisement): void {
    persistDismissedKey(dismissedCampaignKeys, banner.campaign_key)
  }

  return {
    banners,
    visibleBanners,
    isLoading,
    hasError,
    fetchActiveBanners,
    dismissBanner,
  }
}
